<script setup lang="ts">
import { computed, ref } from 'vue'

const props = defineProps<{
  result: Record<string, any>
  skeleton?: boolean
}>()

const debugExpanded = ref(false)

const hasDebug = computed(() => {
  return props.result?.debug != null
})

const displayValue = computed(() => {
  if (!props.result) return '-'

  const r = props.result

  // Formatted value available
  if (r.formatted) return r.formatted

  // Performance metrics (percentage or multiplier)
  if (r.metric) {
    if (r.metric === 'roi') return `${r.value}%`
    return `${r.value}x`
  }

  // Plain value
  if (typeof r.value === 'number') {
    return r.value.toLocaleString()
  }

  // Array values (like ROI by industry)
  if (Array.isArray(r.value)) {
    return `${r.value.length} results`
  }

  // Object values (like entity_count with all types)
  if (typeof r.value === 'object' && r.value !== null) {
    return Object.entries(r.value)
      .map(([k, v]) => `${k}: ${v}`)
      .join(', ')
  }

  return String(r.value)
})

const details = computed(() => {
  if (!props.result) return []
  const r = props.result
  const items: { label: string; value: string }[] = []

  if (r.scope) items.push({ label: 'Scope', value: String(r.scope).replace(/_/g, ' ') })
  if (r.year) items.push({ label: 'Year', value: String(r.year) })
  if (r.currency) items.push({ label: 'Currency', value: r.currency })
  if (r.company_count) items.push({ label: 'Companies', value: String(r.company_count) })
  if (r.commitment_count) items.push({ label: 'Commitments', value: String(r.commitment_count) })
  if (r.spv_count) items.push({ label: 'SPVs', value: String(r.spv_count) })
  if (r.investment_count) items.push({ label: 'Investments', value: String(r.investment_count) })
  if (r.total_invested) items.push({ label: 'Total Invested', value: r.total_invested })
  if (r.total_formatted) items.push({ label: 'Total', value: r.total_formatted })

  return items
})
</script>

<template>
  <div class="rounded-lg bg-white p-6 shadow">
    <template v-if="skeleton">
      <div class="animate-pulse">
        <div class="mb-3 h-4 w-24 rounded bg-gray-200"></div>
        <div class="mb-4 h-8 w-48 rounded bg-gray-200"></div>
        <div class="h-3 w-32 rounded bg-gray-200"></div>
      </div>
    </template>
    <template v-else-if="result">
      <p class="mb-1 text-sm uppercase tracking-wide text-gray-500">Result</p>
      <p class="mb-4 text-3xl font-bold text-gray-900">{{ displayValue }}</p>
      <div v-if="details.length" class="flex flex-wrap gap-4">
        <div v-for="detail in details" :key="detail.label" class="text-sm">
          <span class="text-gray-500">{{ detail.label }}:</span>
          <span class="ml-1 font-medium text-gray-700">{{ detail.value }}</span>
        </div>
      </div>

      <!-- Debug Data -->
      <div v-if="hasDebug" class="mt-4 border-t pt-4">
        <button
          class="flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-500"
          @click="debugExpanded = !debugExpanded"
        >
          <span>{{ debugExpanded ? '▼' : '▶' }}</span>
          <span>Source Data</span>
          <span class="ml-1 font-normal text-gray-400"> ({{ Object.keys(result.debug).length }} sections) </span>
        </button>
        <div v-if="debugExpanded" class="mt-3 space-y-3">
          <template v-for="(value, key) in result.debug" :key="key">
            <div class="rounded-md bg-gray-50 p-3">
              <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {{ String(key).replace(/_/g, ' ') }}
              </p>
              <div v-if="Array.isArray(value)" class="overflow-x-auto">
                <template v-if="value.length === 0">
                  <p class="text-xs text-gray-400">No records</p>
                </template>
                <!-- Array of objects → render as table -->
                <table v-else-if="typeof value[0] === 'object' && value[0] !== null" class="min-w-full text-xs">
                  <thead>
                    <tr>
                      <th
                        v-for="col in Object.keys(value[0])"
                        :key="col"
                        class="pb-1 pr-4 text-left font-medium text-gray-500"
                      >
                        {{ col }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, idx) in value" :key="idx" class="border-t border-gray-200">
                      <td
                        v-for="col in Object.keys(value[0])"
                        :key="col"
                        class="whitespace-nowrap py-1 pr-4 text-gray-700"
                      >
                        {{ row[col] }}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <!-- Array of primitives → render as comma-separated list -->
                <p v-else class="break-all font-mono text-xs text-gray-700">
                  {{ value.join(', ') }}
                </p>
              </div>
              <div v-else-if="typeof value === 'object' && value !== null">
                <div v-for="(v, k) in value" :key="k" class="text-xs text-gray-700">
                  <span class="text-gray-500">{{ k }}:</span>
                  <span class="ml-1 font-mono">{{ v }}</span>
                </div>
              </div>
              <p v-else class="font-mono text-xs text-gray-700">{{ value }}</p>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>
